import { CSSProperties, forwardRef, MouseEvent, ReactNode } from 'react';

import { cx } from '@libs/classnames';

import styles from './ContextMenu.module.css';

type Props = {
  className?: string;
  style?: CSSProperties;
  animated?: boolean;
  children: ReactNode;
  onMouseDown?: (e: MouseEvent<HTMLDivElement>) => void;
};

export const ContextMenu = forwardRef<HTMLDivElement, Props>(function ContextMenu(
  { className, style, animated, children, onMouseDown },
  ref,
) {
  return (
    <div
      ref={ref}
      style={style}
      onMouseDown={onMouseDown}
      className={cx(styles.menu, className, animated && styles.is_animated)}
    >
      {children}
    </div>
  );
});

export const ContextMenuItem = forwardRef<
  HTMLButtonElement,
  {
    className?: string;
    textClassName?: string;
    icon?: ReactNode;
    disabled?: boolean;
    highlighted?: boolean;
    hover?: 'light' | 'normal';
    children: ReactNode;
    onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
    onMouseDown?: (e: MouseEvent<HTMLButtonElement>) => void;
    onMouseEnter?: (e: MouseEvent<HTMLButtonElement>) => void;
  }
>(function ContextMenuItem(
  {
    className,
    textClassName,
    icon,
    disabled,
    highlighted,
    hover,
    children,
    onClick,
    onMouseDown,
    onMouseEnter,
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      className={cx(styles.item, className, {
        [styles.disabled]: disabled,
        [styles.is_highlighted]: highlighted,
        [styles.is_light_hover]: hover === 'light',
      })}
      disabled={disabled}
      onClick={onClick}
      onMouseDown={onMouseDown}
      onMouseEnter={onMouseEnter}
    >
      <span className={cx(styles.item__label, textClassName)}>{children}</span>
      <span className={styles.item__icon}>{icon}</span>
    </button>
  );
});

export function ContextMenuHeader({
  className,
  textClassName,
  icon,
  disabled,
  children,
}: {
  className?: string;
  textClassName?: string;
  icon?: ReactNode;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <div
      className={cx(styles.item, styles.is_header, className, {
        [styles.disabled]: disabled,
      })}
    >
      <span className={cx(styles.item__label, textClassName)}>{children}</span>
      <span className={styles.item__icon}>{icon}</span>
    </div>
  );
}

export function ContextMenuSeparator() {
  return <div className={styles.separator} />;
}
